import React, { useState } from 'react';
import { Globe, Copy, Check, ChevronRight, ChevronDown, Search, Shield, ExternalLink, RefreshCw, Server, Link2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const DNS_PROVIDERS = [
  {
    id: 'godaddy', name: 'GoDaddy', color: 'text-emerald-400',
    steps: (domain, target, type) => [
      'Log in to your GoDaddy account at my.godaddy.com',
      'Go to My Products and find your domain',
      'Click DNS or Manage DNS next to your domain',
      `Click Add Record and select type: ${type}`,
      type === 'CNAME'
        ? `Set Name to "${domain.split('.')[0] === 'www' ? 'www' : '@'}" and Value to "${target}"`
        : `Set Name to "@" and Value to "${target}"`,
      'Set TTL to 1 Hour (or lowest available)',
      'Click Save and wait 15-60 minutes for propagation'
    ]
  },
  {
    id: 'cloudflare', name: 'Cloudflare', color: 'text-orange-400',
    steps: (domain, target, type) => [
      'Log in to your Cloudflare dashboard at dash.cloudflare.com',
      'Select your domain from the dashboard',
      'Click DNS in the left sidebar',
      `Click Add Record, select type: ${type}`,
      type === 'CNAME'
        ? `Set Name to "${domain.split('.')[0] === 'www' ? 'www' : '@'}" and Target to "${target}"`
        : `Set Name to "@" and IPv4 address to "${target}"`,
      'Toggle the Proxy status (orange cloud) ON for SSL and CDN',
      'Click Save — changes apply almost instantly on Cloudflare'
    ]
  },
  {
    id: 'namecheap', name: 'Namecheap', color: 'text-red-400',
    steps: (domain, target, type) => [
      'Log in to Namecheap at namecheap.com',
      'Go to Domain List and click Manage next to your domain',
      'Click Advanced DNS tab',
      `Click Add New Record, choose type: ${type}`,
      type === 'CNAME'
        ? `Set Host to "${domain.split('.')[0] === 'www' ? 'www' : '@'}" and Value to "${target}"`
        : `Set Host to "@" and Value to "${target}"`,
      'Set TTL to Automatic',
      'Click the green checkmark to save — propagation takes 15-30 minutes'
    ]
  },
  {
    id: 'wix', name: 'Wix', color: 'text-blue-400',
    steps: (domain, target, type) => [
      'Log in to your Wix account at wix.com',
      'Go to My Domains in your Wix dashboard',
      'Click the 3-dot menu next to your domain → Manage DNS Records',
      `Click + Add Record and select type: ${type}`,
      type === 'CNAME'
        ? `Set Host Name to "${domain.split('.')[0] === 'www' ? 'www' : '@'}" and Points to "${target}"`
        : `Set Host Name to "@" and Value to "${target}"`,
      'Set TTL to 1 Hour',
      'Click Save — Wix DNS changes can take up to 48 hours'
    ]
  },
  {
    id: 'google', name: 'Google Domains', color: 'text-yellow-400',
    steps: (domain, target, type) => [
      'Go to domains.google.com and sign in',
      'Select your domain from the list',
      'Click DNS in the left menu',
      'Scroll to Custom records section',
      `Click Manage custom records → Create new record → Type: ${type}`,
      type === 'CNAME'
        ? `Set Host name to "${domain.split('.')[0] === 'www' ? 'www' : ''}", Data to "${target}"`
        : `Leave Host name blank, Data to "${target}"`,
      'Click Save — propagation typically takes 5-30 minutes'
    ]
  }
];

const DomainTab = ({ customDomain, setCustomDomain, domainSaved, setDomainSaved, savingDomain, handleSaveDomain }) => {
  const inputClass = "bg-background border-input text-white placeholder:text-muted-foreground focus:border-blue-500/50";

  const [hostingTarget, setHostingTarget] = useState('');
  const [recordType, setRecordType] = useState('CNAME');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [wizardStep, setWizardStep] = useState(1);

  const cleanDomain = customDomain.replace('https://', '').replace('http://', '').replace(/\/$/, '');

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast({ title: 'Copied to clipboard' });
  };

  const handleVerifyDns = async () => {
    if (!cleanDomain) { toast({ title: 'Error', description: 'Enter your domain first', variant: 'destructive' }); return; }
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await api.post('/user/domain/verify-dns', {
        domain: cleanDomain,
        expected_target: hostingTarget,
        record_type: recordType
      });
      setVerifyResult(res.data);
    } catch (err) {
      setVerifyResult({ verified: false, message: err.response?.data?.detail || 'Verification failed. Please try again.' });
    } finally { setVerifying(false); }
  };

  const callbackUrls = customDomain ? [
    { provider: 'Google', url: `${customDomain}/auth/callback`, portal: 'Google Cloud Console' },
    { provider: 'GitHub', url: `${customDomain}/auth/callback/github`, portal: 'GitHub Developer Settings' },
    { provider: 'Facebook', url: `${customDomain}/auth/callback/facebook`, portal: 'Facebook Developers' },
    { provider: 'Twitter/X', url: `${customDomain}/auth/callback/twitter`, portal: 'X Developer Portal' }
  ] : [];

  const dnsRecord = recordType === 'CNAME'
    ? { type: 'CNAME', host: cleanDomain.startsWith('www.') ? 'www' : '@', value: hostingTarget || 'your-app.hosting-provider.com' }
    : { type: 'A', host: '@', value: hostingTarget || '0.0.0.0' };

  return (
    <div className="space-y-6">
      {/* Step 1: Domain & Hosting */}
      <Card className="bg-card border-border" data-testid="dns-wizard-step1">
        <CardHeader className="cursor-pointer" onClick={() => setWizardStep(wizardStep === 1 ? 0 : 1)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${wizardStep >= 1 ? 'bg-blue-600 text-white' : 'bg-accent text-muted-foreground'}`}>1</div>
              <div>
                <CardTitle className="text-white text-lg">Domain & Hosting Target</CardTitle>
                <CardDescription className="text-muted-foreground">Enter your domain and where it should point to</CardDescription>
              </div>
            </div>
            {wizardStep === 1 ? <ChevronDown className="w-5 h-5 text-muted-foreground" /> : <ChevronRight className="w-5 h-5 text-muted-foreground" />}
          </div>
        </CardHeader>
        {wizardStep === 1 && (
          <CardContent className="space-y-5 pt-0">
            <div>
              <Label className="text-foreground/80 text-sm font-medium">Your Custom Domain</Label>
              <div className="flex gap-2 mt-1.5">
                <Input data-testid="custom-domain-input" placeholder="https://myapp.com"
                  value={customDomain} onChange={(e) => { setCustomDomain(e.target.value); setDomainSaved(false); setVerifyResult(null); }}
                  className={`flex-1 ${inputClass}`} />
                <Button data-testid="save-domain-btn" onClick={handleSaveDomain} disabled={savingDomain} className="bg-blue-600 hover:bg-blue-500 min-w-[90px]">
                  {savingDomain ? 'Saving...' : domainSaved ? <><Check className="w-4 h-4 mr-1" />Saved</> : 'Save'}
                </Button>
              </div>
            </div>

            <div className="p-4 bg-muted rounded-xl border border-input">
              <div className="flex items-center gap-2 mb-3">
                <Server className="w-4 h-4 text-blue-400" />
                <span className="text-white text-sm font-medium">Where is your project hosted?</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Enter the IP address or hostname from your hosting provider (Vercel, Railway, AWS, Netlify, etc.)
              </p>

              <div className="flex gap-3 mb-3">
                <button onClick={() => setRecordType('CNAME')} data-testid="select-cname"
                  className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${recordType === 'CNAME' ? 'border-blue-500 bg-blue-500/10' : 'border-border hover:border-input'}`}>
                  <div className="text-sm font-medium text-foreground">CNAME Record</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Points to a hostname (e.g. app.vercel.app)</div>
                </button>
                <button onClick={() => setRecordType('A')} data-testid="select-a-record"
                  className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${recordType === 'A' ? 'border-blue-500 bg-blue-500/10' : 'border-border hover:border-input'}`}>
                  <div className="text-sm font-medium text-foreground">A Record</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Points to an IP address (e.g. 76.76.21.21)</div>
                </button>
              </div>

              <Input data-testid="hosting-target-input"
                placeholder={recordType === 'CNAME' ? 'e.g. cname.vercel-dns.com or myapp.railway.app' : 'e.g. 76.76.21.21'}
                value={hostingTarget} onChange={(e) => { setHostingTarget(e.target.value); setVerifyResult(null); }}
                className={inputClass} />

              <div className="mt-3 flex flex-wrap gap-2">
                {[
                  { label: 'Vercel', val: 'cname.vercel-dns.com', type: 'CNAME' },
                  { label: 'Railway', val: '.up.railway.app', type: 'CNAME' },
                  { label: 'Netlify', val: '.netlify.app', type: 'CNAME' },
                  { label: 'Render', val: '.onrender.com', type: 'CNAME' },
                ].map(hint => (
                  <button key={hint.label} onClick={() => { setHostingTarget(hint.val); setRecordType(hint.type); }}
                    className="text-xs px-2.5 py-1 rounded-md bg-accent text-muted-foreground hover:text-foreground hover:bg-accent transition-colors border border-input">
                    {hint.label}: <span className="text-blue-400 font-mono">{hint.val}</span>
                  </button>
                ))}
              </div>
            </div>

            {customDomain && hostingTarget && (
              <Button onClick={() => setWizardStep(2)} className="w-full bg-blue-600 hover:bg-blue-500" data-testid="continue-to-step2">
                Continue to DNS Setup <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </CardContent>
        )}
      </Card>

      {/* Step 2: DNS Records & Provider Guide */}
      <Card className={`bg-card border-border transition-opacity ${!customDomain || !hostingTarget ? 'opacity-40 pointer-events-none' : ''}`} data-testid="dns-wizard-step2">
        <CardHeader className="cursor-pointer" onClick={() => customDomain && hostingTarget && setWizardStep(wizardStep === 2 ? 0 : 2)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${wizardStep >= 2 ? 'bg-blue-600 text-white' : 'bg-accent text-muted-foreground'}`}>2</div>
              <div>
                <CardTitle className="text-white text-lg">Configure DNS Records</CardTitle>
                <CardDescription className="text-muted-foreground">Add this record at your DNS provider</CardDescription>
              </div>
            </div>
            {wizardStep === 2 ? <ChevronDown className="w-5 h-5 text-muted-foreground" /> : <ChevronRight className="w-5 h-5 text-muted-foreground" />}
          </div>
        </CardHeader>
        {wizardStep === 2 && (
          <CardContent className="space-y-5 pt-0">
            {/* Generated DNS Record */}
            <div className="p-4 bg-background rounded-xl border border-blue-500/30">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-blue-400">DNS Record to Add</h4>
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">{dnsRecord.type} Record</Badge>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Type', value: dnsRecord.type },
                  { label: 'Host / Name', value: dnsRecord.host },
                  { label: 'Value / Points to', value: dnsRecord.value }
                ].map(field => (
                  <div key={field.label}>
                    <div className="text-xs text-muted-foreground mb-1">{field.label}</div>
                    <div className="flex items-center gap-2">
                      <code className="text-sm text-emerald-400 font-mono bg-muted px-2 py-1 rounded flex-1 break-all">{field.value}</code>
                      <button onClick={() => copyToClipboard(field.value, field.label)} className="text-muted-foreground hover:text-foreground shrink-0">
                        {copiedField === field.label ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* DNS Provider Guides */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Select your DNS provider for step-by-step instructions:</h4>
              <div className="flex flex-wrap gap-2 mb-4">
                {DNS_PROVIDERS.map(p => (
                  <button key={p.id} onClick={() => setSelectedProvider(selectedProvider === p.id ? null : p.id)} data-testid={`dns-provider-${p.id}`}
                    className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${selectedProvider === p.id ? 'border-blue-500 bg-blue-500/10 text-white' : 'border-border text-muted-foreground hover:border-input hover:text-foreground'}`}>
                    {p.name}
                  </button>
                ))}
              </div>

              {selectedProvider && (
                <div className="p-4 bg-muted rounded-xl border border-input animate-in fade-in duration-200" data-testid="dns-provider-guide">
                  <h4 className={`font-semibold text-sm mb-3 ${DNS_PROVIDERS.find(p => p.id === selectedProvider)?.color}`}>
                    {DNS_PROVIDERS.find(p => p.id === selectedProvider)?.name} Setup Guide
                  </h4>
                  <ol className="space-y-2.5">
                    {DNS_PROVIDERS.find(p => p.id === selectedProvider)?.steps(cleanDomain, hostingTarget, recordType).map((step, i) => (
                      <li key={i} className="flex gap-3 text-sm">
                        <span className="w-6 h-6 rounded-full bg-accent text-muted-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{i + 1}</span>
                        <span className="text-foreground/80">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>

            <Button onClick={() => setWizardStep(3)} className="w-full bg-blue-600 hover:bg-blue-500" data-testid="continue-to-step3">
              I've added the DNS record <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Step 3: Verify DNS */}
      <Card className={`bg-card border-border transition-opacity ${!customDomain || !hostingTarget ? 'opacity-40 pointer-events-none' : ''}`} data-testid="dns-wizard-step3">
        <CardHeader className="cursor-pointer" onClick={() => customDomain && hostingTarget && setWizardStep(wizardStep === 3 ? 0 : 3)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${wizardStep >= 3 ? 'bg-blue-600 text-white' : 'bg-accent text-muted-foreground'}`}>3</div>
              <div>
                <CardTitle className="text-white text-lg">Verify & Finish</CardTitle>
                <CardDescription className="text-muted-foreground">Check DNS propagation and finalize setup</CardDescription>
              </div>
            </div>
            {wizardStep === 3 ? <ChevronDown className="w-5 h-5 text-muted-foreground" /> : <ChevronRight className="w-5 h-5 text-muted-foreground" />}
          </div>
        </CardHeader>
        {wizardStep === 3 && (
          <CardContent className="space-y-5 pt-0">
            <div className="p-4 bg-muted rounded-xl border border-input">
              <div className="flex items-center gap-2 mb-2">
                <Search className="w-4 h-4 text-blue-400" />
                <span className="text-white text-sm font-medium">DNS Verification</span>
              </div>
              <p className="text-xs text-muted-foreground mb-4">Check if your DNS records have propagated. This can take 5 minutes to 48 hours.</p>
              <Button onClick={handleVerifyDns} disabled={verifying} className="bg-blue-600 hover:bg-blue-500 gap-2" data-testid="verify-dns-btn">
                {verifying ? <><RefreshCw className="w-4 h-4 animate-spin" /> Checking...</> : <><Search className="w-4 h-4" /> Verify DNS Records</>}
              </Button>
            </div>

            {verifyResult && (
              <div className={`p-4 rounded-xl border ${verifyResult.verified ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`} data-testid="verify-dns-result">
                <div className="flex items-center gap-2 mb-2">
                  {verifyResult.verified
                    ? <Check className="w-5 h-5 text-emerald-400" />
                    : <RefreshCw className="w-5 h-5 text-amber-400" />}
                  <span className={`font-semibold text-sm ${verifyResult.verified ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {verifyResult.verified ? 'DNS Verified' : 'Not Yet Propagated'}
                  </span>
                </div>
                <p className="text-foreground/80 text-sm">{verifyResult.message}</p>
                {verifyResult.records && verifyResult.records.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">Records found:</p>
                    {verifyResult.records.map((r, i) => (
                      <div key={i} className="text-xs font-mono text-muted-foreground bg-background px-2 py-1 rounded">
                        {r.type}: {r.value}
                      </div>
                    ))}
                  </div>
                )}
                {!verifyResult.verified && (
                  <p className="text-xs text-muted-foreground mt-3">DNS propagation can take up to 48 hours. Try again in a few minutes.</p>
                )}
              </div>
            )}

            {/* SSL Info */}
            <div className="p-4 bg-muted rounded-xl border border-input">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span className="text-white text-sm font-medium">SSL / HTTPS</span>
              </div>
              <p className="text-sm text-muted-foreground">Most hosting providers automatically provision SSL certificates once DNS is configured. Check your hosting dashboard:</p>
              <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                <li><strong className="text-muted-foreground">Vercel</strong> — Automatic SSL, no action needed</li>
                <li><strong className="text-muted-foreground">Cloudflare</strong> — Enable "Full (Strict)" SSL mode</li>
                <li><strong className="text-muted-foreground">Railway</strong> — Automatic SSL after domain verification</li>
                <li><strong className="text-muted-foreground">AWS</strong> — Use ACM (Certificate Manager) + CloudFront or ALB</li>
              </ul>
            </div>

            {/* OAuth Callback URLs */}
            {callbackUrls.length > 0 && (
              <div className="p-4 bg-muted rounded-xl border border-input">
                <div className="flex items-center gap-2 mb-3">
                  <Link2 className="w-4 h-4 text-violet-400" />
                  <span className="text-white text-sm font-medium">Update OAuth Callback URLs</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">After your domain is live, update these callback URLs in each provider's developer console.</p>
                <div className="space-y-2" data-testid="callback-urls-list">
                  {callbackUrls.map(item => (
                    <div key={item.provider} className="flex items-center justify-between p-2.5 bg-background rounded-lg">
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-muted-foreground">{item.provider} ({item.portal})</span>
                        <code className="text-xs text-emerald-400 font-mono block truncate">{item.url}</code>
                      </div>
                      <button onClick={() => copyToClipboard(item.url, item.provider)} className="text-muted-foreground hover:text-foreground ml-2 shrink-0"
                        data-testid={`copy-url-${item.provider.toLowerCase().replace('/', '')}`}>
                        {copiedField === item.provider ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Helpful Links */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <h4 className="text-white text-sm font-medium mb-3">Helpful Resources</h4>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'DNS Propagation Checker', url: 'https://www.whatsmydns.net/' },
              { label: 'SSL Certificate Checker', url: 'https://www.ssllabs.com/ssltest/' },
              { label: 'GoDaddy DNS Help', url: 'https://www.godaddy.com/help/manage-dns-records-680' },
              { label: 'Cloudflare DNS Docs', url: 'https://developers.cloudflare.com/dns/' },
            ].map(link => (
              <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 p-2.5 rounded-lg text-xs text-muted-foreground hover:text-blue-400 hover:bg-muted transition-colors border border-transparent hover:border-input">
                <ExternalLink className="w-3.5 h-3.5 shrink-0" /> {link.label}
              </a>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DomainTab;
